import { AfterContentInit, AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { ShadowDomConfig } from '@app/core/directives/shadow-dom.directive';

@Component({
  selector: 'app-resource-chip',
  templateUrl: './resource-chip.component.html',
  styleUrls: ['./resource-chip.component.scss']
})
export class ResourceChipComponent implements AfterContentInit, AfterViewInit, OnDestroy {
  @Input() resourceName: string;
  @Input() resourceUrl?: string; // Optional - if not provided, shows text instead of link
  @Input() hasPopoverContent = false;
  @Input() disabled: boolean = false;
  @Input() dismissible: boolean = true; // Controls whether the close button is shown

  @Output() chipRemoved = new EventEmitter<void>();

  @ViewChild('chipContent', { static: false }) chipContent: ElementRef;

  shadowDomConfig: ShadowDomConfig | ShadowDomConfig[];
  

  constructor(private el: ElementRef, private cdr: ChangeDetectorRef) {}

  ngAfterContentInit(): void {}

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
    
    // Create the shadow DOM config to inject our content
    // Need to wait a tick for Angular to finish rendering the conditional rad-popover
    setTimeout(() => {
      // Get the popover element again after potential re-render
      const finalPopoverElement = this.chipContent.nativeElement.querySelector('rad-popover');
      
      // Build HTML string with styles and content
      let html = `<style>
        .chip-link-button {
          all: unset;
          cursor: pointer;
          color: #4E60EE;
          font-size: 16px;
          pointer-events: auto;
          position: relative;
          text-decoration: none;
          display: inline;
          white-space: normal;
          word-break: break-all;
        }
        .chip-link-button:hover {
          text-decoration: underline;
        }
        .chip-text {
          font-size: 16px;
          color: #464247;
          display: inline;
          white-space: normal;
          word-break: break-all;
        }
        .chip-content-wrapper {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .chip-popover {
          display: inline-flex;
          align-items: center;
          vertical-align: middle;
        }
      </style>
      <span class="chip-content-wrapper">`;
      
      // Add link button if URL provided, otherwise just text
      if (this.resourceUrl) {
        html += `<button class="chip-link-button" data-resource-url="${this.resourceUrl}" data-action="navigate">${this.resourceName}</button>`;
      } else {
        html += `<span class="chip-text">${this.resourceName}</span>`;
      }
      
      // If there's a popover, we need to append it as a DOM element
      if (finalPopoverElement && this.hasPopoverContent) {
        // For now, we'll use a two-config approach: one for HTML, one to append the popover
        this.shadowDomConfig = [
          {
            action: 'replace',
            selector: '.rad-chip__label',
            html: html + '</span>'
          },
          {
            action: 'add',
            selector: '.chip-content-wrapper',
            position: 'append',
            element: finalPopoverElement
          }
        ];
      } else {
        // Just inject the HTML
        html += '</span>';
        this.shadowDomConfig = {
          action: 'replace',
          selector: '.rad-chip__label',
          html: html
        };
      }
    });

    // Listen for custom events from shadow DOM buttons
    this.el.nativeElement.addEventListener('shadowButtonClick', this.handleShadowButtonClick);
  }

  ngOnDestroy(): void {
    // Clean up event listener
    this.el.nativeElement.removeEventListener('shadowButtonClick', this.handleShadowButtonClick);
  }

  private handleShadowButtonClick = (e: CustomEvent) => {
    const { data } = e.detail;
    
    if (data.action === 'navigate' && data.resourceUrl) {
      // Open the resource URL in a new tab
      window.open(data.resourceUrl, '_blank');
    }
  };

  onChipRemoved(): void {
    this.chipRemoved.emit();
  }
}

